import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUsers,
  FiPlus,
  FiSearch,
  FiFilter,
  FiGrid,
  FiList,
  FiEye,
  FiEdit3,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiPhone,
  FiMail,
  FiMapPin,
  FiBriefcase,
  FiCalendar,
  FiBookOpen,
  FiX,
  FiRefreshCw,
  FiPrinter
} from "react-icons/fi";
import { FaStore, FaPrint } from "react-icons/fa";
import FilterModal from "./FilterModel";
import axios from "axios";
import Swal from "sweetalert2";

import backgroundkartu from "../../assets/images/kartu1.png";
import backgroundkartu2 from "../../assets/images/kartu2.png";

const KaryawanPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const [currentKaryawan, setCurrentKaryawan] = useState([]);
  const [totalKaryawan, setTotalKaryawan] = useState(0);
  const [selectedKaryawan, setSelectedKaryawan] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [globalFilter, setGlobalFilter] = useState("");
  const [userRole, setUserRole] = useState(null);
  const [showAccessDenied, setShowAccessDenied] = useState(false);

  // Card Print Modal State
  const [showCardModal, setShowCardModal] = useState(false);
  const [cardKaryawan, setCardKaryawan] = useState(null);

  useEffect(() => {
    const role = (localStorage.getItem("role") || "").toLowerCase();
    setUserRole(role);
    if (role && !["owner", "admin", "superadmin", "manager"].includes(role)) {
      setShowAccessDenied(true);
    }
  }, []);

  const handleApplyFilters = (appliedFilters) => {
    setFilters(appliedFilters);
    setIsFilterOpen(false);
    fetchKaryawan(appliedFilters);
  };

  const fetchKaryawan = async (appliedFilters = {}) => {
    setIsLoading(true);
    try {
      if (!token) return;

      const params = new URLSearchParams({
        per_page: appliedFilters.per_page || perPage,
        page: appliedFilters.page || currentPage,
        order_by: appliedFilters.order_by || "created_at",
        search: appliedFilters.search !== undefined ? appliedFilters.search : globalFilter,
      }).toString();

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/employee?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        const rawData = response.data.data || [];
        const karyawanData = Array.isArray(rawData) ? rawData : [];
        const totalData = response.data.meta?.total || karyawanData.length;
        const lastPage = response.data.meta?.last_page || Math.ceil(totalData / perPage) || 1;

        setCurrentKaryawan(karyawanData);
        setTotalKaryawan(totalData);
        setTotalPages(lastPage);
      }
    } catch (error) {
      console.error("Failed to fetch karyawan:", error);
      setCurrentKaryawan([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchKaryawan({ search: globalFilter, page: currentPage });
  }, [globalFilter, currentPage]);

  const handleDetail = (karyawan) => {
    setSelectedKaryawan(karyawan);
  };

  const closeDetail = () => {
    setSelectedKaryawan(null);
  };

  const handleEdit = (id) => {
    navigate(`/enterprise/karyawan/editkaryawan/${id}`);
  };

  const handlePrintCard = (karyawan) => {
    setCardKaryawan(karyawan);
    setShowCardModal(true);
  };

  const executePrint = () => {
    window.print();
  };

  const handleDelete = async (karyawan) => {
    const result = await Swal.fire({
      title: `Hapus "${karyawan.full_name || karyawan.name}"?`,
      text: "Data karyawan akan terhapus secara permanen!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Ya, Hapus!",
      cancelButtonText: "Batal",
      customClass: { popup: "rounded-3xl" },
    });

    if (!result.isConfirmed) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/employee/delete/${karyawan.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Gagal menghapus karyawan");
      }

      await Swal.fire({
        title: "Terhapus!",
        text: `Data karyawan "${karyawan.full_name || karyawan.name}" telah dihapus.`,
        icon: "success",
        timer: 1800,
        showConfirmButton: false,
        customClass: { popup: "rounded-3xl" },
      });

      if (selectedKaryawan?.id === karyawan.id) {
        setSelectedKaryawan(null);
      }

      fetchKaryawan();
    } catch (error) {
      Swal.fire({
        title: "Gagal Menghapus",
        text: error.message,
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    }
  };

  const maleCount = currentKaryawan.filter(
    (k) => (k.gender || "").toLowerCase() === "laki-laki" || (k.gender || "").toLowerCase() === "l"
  ).length;

  const femaleCount = currentKaryawan.filter(
    (k) => (k.gender || "").toLowerCase() === "perempuan" || (k.gender || "").toLowerCase() === "p"
  ).length;

  return (
    <div className="space-y-6 pb-12">
      {/* 1. TOP METRIC SUMMARY BAR */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-5"
      >
        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Total Karyawan
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900">
              {isLoading ? "..." : totalKaryawan}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FiUsers className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Pria (Laki-laki)
            </p>
            <h2 className="text-3xl font-extrabold text-teal-600">
              {isLoading ? "..." : maleCount}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-teal-50 text-teal-600 border border-teal-100">
            <FiUsers className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Wanita (Perempuan)
            </p>
            <h2 className="text-3xl font-extrabold text-rose-600">
              {isLoading ? "..." : femaleCount}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-rose-50 text-rose-600 border border-rose-100">
            <FiUsers className="w-6 h-6" />
          </div>
        </div>
      </motion.div>

      {/* 2. MAIN CONTROLS & ACTION BAR */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Live Search */}
          <div className="relative w-full md:w-80">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Cari nama karyawan, email, no HP..."
              value={globalFilter || ""}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-semibold"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
            {/* View Mode Toggle */}
            <div className="flex items-center p-1 bg-gray-100 rounded-2xl border border-gray-200/80">
              <button
                onClick={() => setViewMode("grid")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  viewMode === "grid"
                    ? "bg-white text-emerald-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                <FiGrid className="w-3.5 h-3.5" />
                <span>Grid</span>
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  viewMode === "table"
                    ? "bg-white text-emerald-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                <FiList className="w-3.5 h-3.5" />
                <span>Tabel</span>
              </button>
            </div>

            {/* Shift Karyawan CTA */}
            <button
              onClick={() => navigate("/enterprise/karyawan/shiftingkaryawan")}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-2xl transition-all shadow-sm"
            >
              <FiRefreshCw className="w-4 h-4 text-emerald-600" />
              <span>Shift Matriks</span>
            </button>

            {/* Filter Modal Toggle */}
            <button
              onClick={() => setIsFilterOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-2xl transition-all shadow-sm"
            >
              <FiFilter className="w-4 h-4 text-emerald-600" />
              <span>Filter</span>
            </button>

            {/* Create Karyawan CTA (GREEN BUTTON) */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/enterprise/karyawan/createkaryawan")}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-2xl shadow-lg shadow-emerald-600/20 transition-all"
            >
              <FiPlus className="w-4 h-4" />
              <span>Tambah Karyawan</span>
            </motion.button>
          </div>
        </div>

        {/* 3. CONTENT AREA (GRID VS TABLE) */}
        {isLoading ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : currentKaryawan.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            <FiUsers className="w-12 h-12 mx-auto mb-2 text-gray-300 stroke-1" />
            <p className="font-semibold text-gray-600">Belum ada karyawan terdaftar</p>
            <p className="text-xs text-gray-400 mt-1">Klik tombol 'Tambah Karyawan' untuk menambahkan data baru.</p>
          </div>
        ) : viewMode === "grid" ? (
          /* GRID CARD VIEW */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {currentKaryawan.map((item) => {
              const fullName = item.full_name || item.fullname || item.name || "Karyawan";
              const outletName = item.outlet?.name || item.penempatan || "Outlet Pusat";
              const roleName = item.role || item.position || item.jabatan || "Staff";

              return (
                <motion.div
                  key={item.id}
                  whileHover={{ y: -4 }}
                  className="bg-white rounded-3xl p-5 border border-gray-100 shadow-xl shadow-gray-200/40 flex flex-col justify-between space-y-4 hover:border-emerald-200 transition-all duration-300 relative overflow-hidden"
                >
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 font-extrabold flex items-center justify-center text-sm shadow-sm flex-shrink-0">
                          {fullName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-base leading-tight">
                            {fullName}
                          </h3>
                          <span className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                            <FaStore className="w-3 h-3 text-emerald-600" />
                            {outletName}
                          </span>
                        </div>
                      </div>

                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">
                        {roleName}
                      </span>
                    </div>

                    <div className="space-y-2 pt-2 text-xs text-gray-600 border-t border-gray-50">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">No. Telepon</span>
                        <span className="font-mono text-gray-700">
                          {item.no_hp || item.phone || "-"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Email</span>
                        <span className="text-gray-700 truncate max-w-[160px]">
                          {item.email || "-"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Pendidikan</span>
                        <span className="font-semibold text-gray-800">
                          {item.pendidikan || "-"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handleDetail(item)}
                      className="flex-1 py-2 px-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold transition-all flex items-center justify-center gap-1.5"
                    >
                      <FiEye className="w-3.5 h-3.5" />
                      <span>Detail</span>
                    </button>
                    <button
                      onClick={() => handlePrintCard(item)}
                      className="p-2 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-600 transition-all"
                      title="Cetak Kartu ID"
                    >
                      <FiPrinter className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(item.id)}
                      className="p-2 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-600 transition-all"
                      title="Edit"
                    >
                      <FiEdit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      className="p-2 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 transition-all"
                      title="Hapus"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          /* TABLE VIEW */
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-left text-xs text-gray-600">
              <thead>
                <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                  <th className="p-3.5 w-12 text-center">No</th>
                  <th className="p-3.5">Nama Karyawan</th>
                  <th className="p-3.5">Penempatan Outlet</th>
                  <th className="p-3.5">Posisi / Role</th>
                  <th className="p-3.5">No. HP</th>
                  <th className="p-3.5">Email</th>
                  <th className="p-3.5 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentKaryawan.map((item, idx) => {
                  const fullName = item.full_name || item.fullname || item.name || "Karyawan";
                  const outletName = item.outlet?.name || item.penempatan || "Outlet Pusat";
                  const roleName = item.role || item.position || item.jabatan || "Staff";

                  return (
                    <tr key={item.id} className="hover:bg-emerald-50/30 transition-colors">
                      <td className="p-3.5 text-center font-semibold text-gray-400">
                        {(currentPage - 1) * perPage + idx + 1}
                      </td>
                      <td className="p-3.5 font-bold text-gray-900 flex items-center gap-2">
                        <div className="w-7 h-7 rounded-xl bg-emerald-100 text-emerald-700 font-bold flex items-center justify-center text-xs flex-shrink-0">
                          {fullName.charAt(0).toUpperCase()}
                        </div>
                        <span>{fullName}</span>
                      </td>
                      <td className="p-3.5 font-semibold text-gray-700">{outletName}</td>
                      <td className="p-3.5">
                        <span className="inline-flex px-2.5 py-0.5 rounded-lg text-[11px] font-bold capitalize bg-emerald-50 text-emerald-700 border border-emerald-100">
                          {roleName}
                        </span>
                      </td>
                      <td className="p-3.5 font-mono text-gray-600">{item.no_hp || item.phone || "-"}</td>
                      <td className="p-3.5 text-gray-600">{item.email || "-"}</td>
                      <td className="p-3.5 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleDetail(item)}
                            className="p-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition-all"
                            title="Detail"
                          >
                            <FiEye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handlePrintCard(item)}
                            className="p-2 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-600 transition-all"
                            title="Cetak Kartu ID"
                          >
                            <FiPrinter className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(item.id)}
                            className="p-2 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-600 transition-all"
                            title="Edit"
                          >
                            <FiEdit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item)}
                            className="p-2 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 transition-all"
                            title="Hapus"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION CONTROLS */}
        <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-gray-100 gap-3 text-xs">
          <p className="text-gray-400">
            Menampilkan <span className="font-bold text-gray-700">{currentKaryawan.length}</span> dari{" "}
            <span className="font-bold text-gray-700">{totalKaryawan}</span> karyawan
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-semibold text-gray-600 px-2">
              Halaman {currentPage} dari {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* 4. SIDE DETAIL SLIDE-OVER DRAWER */}
      <AnimatePresence>
        {selectedKaryawan && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeDetail}
              className="fixed inset-0 bg-gray-900/40 backdrop-blur-xs z-40"
            />

            {/* Slide Drawer */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 250 }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 p-6 overflow-y-auto flex flex-col justify-between border-l border-gray-100"
            >
              <div className="space-y-6">
                {/* Header Drawer */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-emerald-600 text-white font-extrabold flex items-center justify-center shadow-lg shadow-emerald-600/20">
                      <FiUsers className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">Profil Karyawan</h2>
                      <span className="text-xs text-gray-400">ID: #{selectedKaryawan.id}</span>
                    </div>
                  </div>
                  <button
                    onClick={closeDetail}
                    className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>

                {/* Profile Card Header */}
                <div className="p-4 rounded-3xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-600 text-white font-extrabold text-lg flex items-center justify-center shadow-md">
                      {(selectedKaryawan.full_name || selectedKaryawan.fullname || "K").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-lg font-extrabold text-gray-900">
                        {selectedKaryawan.full_name || selectedKaryawan.fullname}
                      </h3>
                      <span className="text-xs text-gray-500 font-semibold">
                        {selectedKaryawan.role || selectedKaryawan.position || "Staff"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Specs List */}
                <div className="space-y-3 text-xs">
                  <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100 flex items-center gap-3">
                    <FaStore className="text-emerald-600 w-4 h-4" />
                    <div>
                      <p className="text-gray-400 font-medium">Penempatan Outlet</p>
                      <p className="font-bold text-gray-800">
                        {selectedKaryawan.outlet?.name || selectedKaryawan.penempatan || "Pusat"}
                      </p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100 flex items-center gap-3">
                    <FiPhone className="text-emerald-600 w-4 h-4" />
                    <div>
                      <p className="text-gray-400 font-medium">Nomor Handphone</p>
                      <p className="font-bold text-gray-800">{selectedKaryawan.no_hp || selectedKaryawan.phone || "-"}</p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100 flex items-center gap-3">
                    <FiMail className="text-emerald-600 w-4 h-4" />
                    <div>
                      <p className="text-gray-400 font-medium">Alamat Email</p>
                      <p className="font-bold text-gray-800">{selectedKaryawan.email || "-"}</p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100 flex items-center gap-3">
                    <FiBookOpen className="text-emerald-600 w-4 h-4" />
                    <div>
                      <p className="text-gray-400 font-medium">Pendidikan Terakhir</p>
                      <p className="font-bold text-gray-800">{selectedKaryawan.pendidikan || "-"}</p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100 flex items-center gap-3">
                    <FiCalendar className="text-emerald-600 w-4 h-4" />
                    <div>
                      <p className="text-gray-400 font-medium">Tanggal Bergabung (Join Date)</p>
                      <p className="font-bold text-gray-800">{selectedKaryawan.join_date || "-"}</p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100">
                    <p className="text-gray-400 font-medium mb-1">Alamat Tempat Tinggal</p>
                    <p className="font-semibold text-gray-800 leading-relaxed">
                      {selectedKaryawan.alamat || "Alamat tidak diisi"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Drawer Footer Buttons */}
              <div className="pt-6 border-t border-gray-100 grid grid-cols-2 gap-3">
                <button
                  onClick={() => handlePrintCard(selectedKaryawan)}
                  className="py-3 px-3 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs shadow-lg shadow-blue-600/20 transition-all flex items-center justify-center gap-2"
                >
                  <FiPrinter className="w-4 h-4" />
                  <span>Cetak Kartu</span>
                </button>

                <button
                  onClick={() => handleEdit(selectedKaryawan.id)}
                  className="py-3 px-3 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs shadow-lg shadow-amber-500/20 transition-all flex items-center justify-center gap-2"
                >
                  <FiEdit3 className="w-4 h-4" />
                  <span>Edit Data</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* 5. CETAK KARTU ID KARYAWAN MODAL */}
      <AnimatePresence>
        {showCardModal && cardKaryawan && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.25 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100 flex flex-col"
            >
              {/* Header Card Modal */}
              <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-2xl bg-emerald-600 text-white shadow-md shadow-emerald-500/20">
                    <FiPrinter className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Kartu ID Karyawan</h2>
                    <p className="text-xs text-gray-500">Pratinjau cetak kartu identitas resmi Apotek Djaya Medika</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCardModal(false)}
                  className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-white/80 transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              {/* Body ID Card Preview */}
              <div className="p-6 space-y-6 flex flex-col items-center justify-center bg-gray-50/50">
                {/* ID Card Display */}
                <div className="w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl border border-emerald-200 relative bg-white text-gray-800">
                  {/* Front View */}
                  <div className="relative p-6 bg-gradient-to-br from-emerald-700 via-teal-700 to-emerald-900 text-white min-h-[220px] flex flex-col justify-between overflow-hidden">
                    {/* Background Pattern Image */}
                    <img
                      src={backgroundkartu}
                      alt="Card BG"
                      className="absolute inset-0 w-full h-full object-cover opacity-20 pointer-events-none"
                    />

                    {/* Top Brand Header */}
                    <div className="relative z-10 flex items-center justify-between border-b border-white/20 pb-3">
                      <div>
                        <h4 className="font-extrabold text-sm tracking-wider uppercase">Apotek Djaya Medika</h4>
                        <p className="text-[10px] text-emerald-200">Sistem Farmasi Enterprise</p>
                      </div>
                      <span className="px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-xs text-[9px] font-extrabold tracking-widest uppercase">
                        OFFICIAL CARD
                      </span>
                    </div>

                    {/* Employee Profile Center */}
                    <div className="relative z-10 my-4 flex items-center gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-white text-emerald-800 font-extrabold text-2xl flex items-center justify-center shadow-lg border-2 border-emerald-300 flex-shrink-0">
                        {(cardKaryawan.full_name || cardKaryawan.fullname || "K").charAt(0).toUpperCase()}
                      </div>
                      <div className="space-y-1">
                        <h3 className="font-extrabold text-base leading-tight text-white drop-shadow-sm">
                          {cardKaryawan.full_name || cardKaryawan.fullname}
                        </h3>
                        <p className="text-xs text-emerald-200 font-semibold">
                          {cardKaryawan.role || cardKaryawan.position || "Staff Apotek"}
                        </p>
                        <p className="text-[10px] text-white/80">
                          ID: #{cardKaryawan.id || "000"} • {cardKaryawan.outlet?.name || cardKaryawan.penempatan || "Pusat"}
                        </p>
                      </div>
                    </div>

                    {/* Footer Contact */}
                    <div className="relative z-10 flex items-center justify-between text-[10px] text-emerald-200 pt-2 border-t border-white/20">
                      <span>HP: {cardKaryawan.no_hp || cardKaryawan.phone || "-"}</span>
                      <span>Join: {cardKaryawan.join_date || "-"}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Modal Actions */}
              <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50/50">
                <button
                  onClick={() => setShowCardModal(false)}
                  className="px-5 py-2.5 rounded-2xl bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs font-semibold transition-colors"
                >
                  Tutup
                </button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={executePrint}
                  className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-2"
                >
                  <FaPrint className="w-4 h-4" />
                  <span>Cetak Sekarang</span>
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* FILTER MODAL */}
      {isFilterOpen && (
        <FilterModal
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
        />
      )}

      {/* ACCESS DENIED OVERLAY */}
      {showAccessDenied && (
        <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white p-6 rounded-3xl shadow-2xl w-full max-w-sm text-center space-y-4">
            <h2 className="text-xl font-bold text-gray-900">Akses Ditolak</h2>
            <p className="text-xs text-gray-500">
              Anda tidak memiliki akses untuk mengelola karyawan.
            </p>
            <button
              onClick={() => navigate("/")}
              className="w-full py-2.5 rounded-2xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700"
            >
              Kembali ke Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default KaryawanPage;